import {
  parseActivePlanGrant,
  parseExpiredSignupGrant,
  parseLapsedSubscription,
  type ActivePlanGrant,
  type LapsedSubscription,
} from '../../../../../shared/billing-catalog';
import {
  normalizeSubscriptionProvider,
  resolveSubscribedPaidTier,
  type PaidTier,
  type SubscriptionProvider,
} from './billingScreen.utils';
import {
  normalizePaidPlanCode,
  type PaidPlanCode,
} from '../feature-flags/planIntentService';

export type { PaidPlanCode };
export type BillingAccessMode = 'trial' | 'free' | 'paid' | 'pending' | 'lapsed';

export type BillingAccessState = {
  mode: BillingAccessMode;
  subscribedTier: PaidTier;
  provider: SubscriptionProvider;
  activeGrant: ActivePlanGrant | null;
  pendingPlan: PaidPlanCode | null;
  expiredSignupGrant: ActivePlanGrant | null;
  lapsedSubscription: LapsedSubscription | null;
  /** Plan a destacar / convertir (grant, pending, lapsed o trial vencido). */
  conversionPlan: PaidPlanCode | null;
};

type EntitlementsLike = {
  plan_code?: string;
  active_plan_grant?: unknown;
  expired_signup_grant?: unknown;
  lapsed_subscription?: unknown;
  subscription_provider?: unknown;
  show_subscription_lapsed_prompt?: boolean;
  show_signup_trial_expired_prompt?: boolean;
} | null | undefined;

/**
 * Modo de acceso comercial para UI de Billing.
 * Prioridad: paid > trial > lapsed > pending > free.
 */
export function resolveBillingAccessState(input: {
  entitlements: EntitlementsLike;
  pendingPlan?: PaidPlanCode | null;
}): BillingAccessState {
  const { entitlements, pendingPlan = null } = input;
  const subscribedTier = resolveSubscribedPaidTier(entitlements);
  const provider = normalizeSubscriptionProvider(
    entitlements?.subscription_provider as string | null | undefined,
  );
  const activeGrant = parseActivePlanGrant(entitlements?.active_plan_grant);
  const expiredSignupGrant = parseExpiredSignupGrant(entitlements?.expired_signup_grant);
  const lapsedSubscription = parseLapsedSubscription(entitlements?.lapsed_subscription);
  const pending = pendingPlan;

  if (provider && (subscribedTier === 'emprende' || subscribedTier === 'crece')) {
    return {
      mode: 'paid',
      subscribedTier,
      provider,
      activeGrant: null,
      pendingPlan: pending,
      expiredSignupGrant,
      lapsedSubscription,
      conversionPlan: null,
    };
  }

  if (activeGrant && !provider) {
    const grantPlan = normalizePaidPlanCode(activeGrant.plan_code);
    return {
      mode: 'trial',
      subscribedTier,
      provider,
      activeGrant,
      pendingPlan: pending,
      expiredSignupGrant,
      lapsedSubscription,
      conversionPlan: grantPlan,
    };
  }

  if (lapsedSubscription) {
    const lapsedPlan = normalizePaidPlanCode(lapsedSubscription.plan_code);
    return {
      mode: 'lapsed',
      subscribedTier,
      provider,
      activeGrant: null,
      pendingPlan: pending,
      expiredSignupGrant,
      lapsedSubscription,
      conversionPlan: lapsedPlan ?? pending,
    };
  }

  if (pending) {
    return {
      mode: 'pending',
      subscribedTier,
      provider,
      activeGrant: null,
      pendingPlan: pending,
      expiredSignupGrant,
      lapsedSubscription,
      conversionPlan: pending,
    };
  }

  if (expiredSignupGrant) {
    const expiredPlan = normalizePaidPlanCode(expiredSignupGrant.plan_code);
    return {
      mode: 'free',
      subscribedTier,
      provider,
      activeGrant: null,
      pendingPlan: null,
      expiredSignupGrant,
      lapsedSubscription,
      conversionPlan: expiredPlan,
    };
  }

  return {
    mode: 'free',
    subscribedTier,
    provider,
    activeGrant: null,
    pendingPlan: null,
    expiredSignupGrant,
    lapsedSubscription,
    conversionPlan: null,
  };
}
